import { Routes } from '@angular/router';
import { listingDetailsAuthGuard } from '@core/guards/listing-details-auth-guard';
import { publicListingDetailsGuard } from '@core/guards/public-listing-details-guard';
import { analyticsLeaveGuard } from './features/analytics/guards/analytics-leave.guard';
import { publicContextGuard } from './features/public/guards/public-context.guard';
import { searchCategoryGuard } from './features/search/guards/search-category.guard';
import { APP_ROUTES } from '@core/routes';
import { authGuard } from './core/auth-guard';

export const routes: Routes = [
    {
        path: '',
        loadComponent: () => import('./features/home/components/home.component').then(m => m.HomeComponent),
        data: { key: 'prnyc-home', pageTitle: 'Home' }
    },
    {
        path: APP_ROUTES.SEARCH,
        loadComponent: () => import('./features/search/search.component').then(m => m.SearchComponent),
        canActivate: [authGuard, searchCategoryGuard],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: { key: 'prnyc-search', pageTitle: 'Search' }
    },
    {
        path: APP_ROUTES.BUILDING_SEARCH,
        loadComponent: () => import('./features/building-search/building-search.component').then(m => m.BuildingSearchComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-building-search', pageTitle: 'Building Search' }
    },
    {
        path: `${APP_ROUTES.LISTING_DETAILS}/:id`,
        loadComponent: () => import('./features/listing-details/listing-details.component').then(m => m.ListingDetailsComponent),
        canActivate: [listingDetailsAuthGuard],
        data: { key: 'prnyc-listing-details', pageTitle: 'Listing Details' }
    },
    {
        path: `${APP_ROUTES.PUBLIC_LISTING_DETAILS}/:id`,
        loadComponent: () => import('./features/listing-details/pages/public-listing-details/public-listing-details.component')
            .then(m => m.PublicListingDetailsComponent),
        canActivate: [publicListingDetailsGuard, publicContextGuard],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: { key: 'prnyc-public-listing-details', pageTitle: 'Listing' }
    },
    {
        path: `${APP_ROUTES.BUILDING_DETAILS}/:id`,
        loadComponent: () => import('./features/building-details/building-details.component').then(m => m.BuildingDetailsComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-building-details', pageTitle: 'Building Details' }
    },
    {
        path: `${APP_ROUTES.PUBLIC_BUILDING_SHARE}/:code`,
        loadComponent: () => import('./features/building-details/pages/public-building-share/public-building-share.component').then(m => m.PublicBuildingShareComponent),
        data: { key: 'prnyc-public-building-share', pageTitle: 'Shared Buildings' }
    },
    {
        path: `${APP_ROUTES.PUBLIC_MORTGAGE_CALCULATOR}/:id`,
        loadComponent: () =>
            import('./features/mortgage-calculator/pages/public-mortgage-calculator/public-mortgage-calculator.component')
                .then(m => m.PublicMortgageCalculatorComponent),
        canActivate: [publicListingDetailsGuard, publicContextGuard],
        data: { key: 'prnyc-public-mortgage-calculator', pageTitle: 'Mortgage Calculator' }
    },
    {
        path: `${APP_ROUTES.PUBLIC_SEARCH}/:code`,
        loadComponent: () =>
            import('./features/search/pages/public-search-results/public-search-results.component')
                .then(m => m.PublicSearchResultsComponent),
        canActivate: [publicContextGuard],
        data: { key: 'prnyc-public-search-share', pageTitle: 'Shared Listings' }
    },
    {
        path: APP_ROUTES.UI_KIT,
        loadComponent: () => import('./features/ui-kit/ui-kit.component').then(m => m.UiKitComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-ui-kit', pageTitle: 'UI Kit' }
    },
    {
        path: APP_ROUTES.DESIGN_LAB,
        loadChildren: () => import('./design-lab/core/design-lab.routes').then(m => m.designLabRoutes),
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        data: { key: 'prnyc-design-lab', pageTitle: 'Design Lab', hideChatbot: true }
    },
    {
        path: APP_ROUTES.CUSTOMERS,
        loadComponent: () => import('./features/customer/pages/customer-list/customer-list.component').then(m => m.CustomerListComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-customers', pageTitle: 'Customers' }
    },
    {
        path: APP_ROUTES.IMAGE_SEARCH,
        loadComponent: () => import('./features/image-search/image-search.component').then(m => m.ImageSearchComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-image-search', pageTitle: 'Image Search' }
    },
    {
        path: APP_ROUTES.PORTFOLIO,
        loadComponent: () => import('./features/portfolio/portfolio.component').then(m => m.PortfolioComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-portfolio', pageTitle: 'Portfolio' }
    },
    {
        path: APP_ROUTES.SAVED_SEARCHES_FOLDER_VIEW,
        loadComponent: () =>
            import('./features/folders/pages/saved-searches-folder-view/saved-searches-folder-view.component')
                .then(m => m.SavedSearchesFolderViewComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-saved-searches-folder-view', pageTitle: 'Saved Searches' }
    },
    {
        path: `${APP_ROUTES.FOLDER_VIEW}/:folderType/:folderId`,
        loadComponent: () =>
            import('./features/folders/pages/folder-view/folder-view.component')
                .then(m => m.FolderViewComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-folder-view', pageTitle: 'Folder' }
    },
    {
        path: `${APP_ROUTES.SAVED_SEARCH_VIEW}/:savedSearchId`,
        loadComponent: () =>
            import('./features/folders/pages/folder-view/folder-view.component')
                .then(m => m.FolderViewComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-saved-search-view', pageTitle: 'Saved Search' }
    },
    {
        path: `${APP_ROUTES.FAVORITE_BUILDING_VIEW}/:savedSearchId`,
        loadComponent: () =>
            import('./features/folders/pages/folder-view/folder-view.component')
                .then(m => m.FolderViewComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-favorite-building-view', pageTitle: 'Favorite Building' }
    },
    {
        path: APP_ROUTES.EXTERNAL_FOLDER_VIEW,
        loadComponent: () =>
            import('./features/external-listings/pages/external-folder-view/external-folder-view.component')
                .then(m => m.ExternalFolderViewComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-external-folder-view', pageTitle: 'External Listings' }
    },
    {
        path: APP_ROUTES.FAVORITE_BUILDINGS_FOLDER_VIEW,
        loadComponent: () =>
            import('./features/favorite-buildings/pages/favorite-buildings-folder-view/favorite-buildings-folder-view.component')
                .then(m => m.FavoriteBuildingsFolderViewComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-favorite-buildings-folder-view', pageTitle: 'Favorite Buildings' }
    },
    {
        path: APP_ROUTES.APPOINTMENTS,
        loadComponent: () => import('./features/appointments/appointments.component').then(m => m.AppointmentsComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-appointments', pageTitle: 'Appointments' }
    },
    {
        path: APP_ROUTES.NOTIFICATIONS,
        loadComponent: () => import('./features/notifications/notifications.component').then(m => m.NotificationsComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-notifications', pageTitle: 'Notifications' }
    },
    {
        path: APP_ROUTES.REPORTS,
        loadComponent: () => import('./features/report/pages/report-management/report-management.component').then(m => m.ReportManagementComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-reports', pageTitle: 'My Reports' }
    },
    {
        path: APP_ROUTES.REPORT_PRINT,
        loadComponent: () => import('./features/report/pages/report-print/report-print.component').then(m => m.ReportPrintComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-report-print', pageTitle: 'Print Report' }
    },
    {
        path: APP_ROUTES.REPORT,
        loadComponent: () => import('./features/report/pages/preview-report/preview-report.component').then(m => m.PreviewReportComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-report', pageTitle: 'Report' }
    },
    {
        path: `${APP_ROUTES.SHARED_REPORT}/:code`,
        loadComponent: () => import('./features/report/pages/shared-report/shared-report.component').then(m => m.SharedReportComponent),
        data: { key: 'prnyc-share', pageTitle: 'Shared Report' }
    },
    {
        path: APP_ROUTES.SHARED_REPORT_PRINT,
        loadComponent: () => import('./features/report/pages/shared-report-print/shared-report-print.component').then(m => m.SharedReportPrintComponent),
        data: { key: 'prnyc-shared-report-print', pageTitle: 'Print Shared Report' }
    },
    {
        path: APP_ROUTES.VIBE_REPORT_EDIT,
        loadComponent: () => import('./features/vibe-report/pages/vibe-report-editor/vibe-report-editor.component').then(m => m.VibeReportEditorComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-vibe-report-edit', pageTitle: 'Vibe Report Editor' }
    },
    {
        path: APP_ROUTES.VIBE_REPORT_VIEW,
        loadComponent: () => import('./features/vibe-report/pages/vibe-report-viewer/vibe-report-viewer.component').then(m => m.VibeReportViewerComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-vibe-report-view', pageTitle: 'Vibe Report' }
    },
    {
        path: `${APP_ROUTES.CUSTOMER}/:id`,
        loadComponent: () => import('./features/customer/pages/customer-details/customer-details.component').then(m => m.CustomerDetailsComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-customer-details', pageTitle: 'Customer' }
    },
    {
        path: APP_ROUTES.MARKET_ACTIVITY,
        loadComponent: () => import('./features/market-activity/components/market-activity-page/market-activity-page.component').then(m => m.MarketActivityPageComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-market-activity', pageTitle: 'Market Activity' }
    },
    {
        path: APP_ROUTES.ANALYTICS,
        loadComponent: () => import('./features/analytics/pages/analytics-page/analytics-page.component').then(m => m.AnalyticsPageComponent),
        canActivate: [authGuard],
        canDeactivate: [analyticsLeaveGuard],
        data: { key: 'prnyc-analytics', pageTitle: 'Analytics' }
    },
    {
        path: APP_ROUTES.SAVED_SEARCHES,
        loadComponent: () => import('./features/saved-search/pages/saved-searches/saved-searches.component').then(m => m.SavedSearchesComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-saved-searches', pageTitle: 'Saved Searches' }
    },
    {
        path: APP_ROUTES.AGENT_HUB,
        loadComponent: () => import('./features/agent-hub/pages/agent-hub/agent-hub.component').then(m => m.AgentHubComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-agent-hub', pageTitle: 'Agent Hub' }
    },
    {
        path: APP_ROUTES.NEIGHBORHOOD_GUIDE,
        loadComponent: () => import('./features/neighborhood-guide/pages/neighborhood-guide/neighborhood-guide.component').then(m => m.NeighborhoodGuideComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-neighborhood-guide', pageTitle: 'Neighborhood Guide' }
    },
    {
        path: `${APP_ROUTES.NEIGHBORHOOD_DETAILS}/:id`,
        loadComponent: () => import('./features/neighborhood-details/neighborhood-details.component').then(m => m.NeighborhoodDetailsComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-neighborhood-details', pageTitle: 'Neighborhood' }
    },
    {
        path: APP_ROUTES.CMA_SEARCH,
        loadComponent: () => import('./features/cma/comparable-search/components/comparable-search/comparable-search.component').then(m => m.ComparableSearchComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-cma-search', pageTitle: 'Comparable Search' }
    },
    {
        path: APP_ROUTES.CMA_RESULTS,
        loadComponent: () => import('./features/cma/comparable-results/pages/comparable-results-page/comparable-results-page.component').then(m => m.ComparableResultsPageComponent),
        canActivate: [authGuard],
        data: { key: 'prnyc-cma-results', pageTitle: 'Comparable Results' }
    },
    {
        path: '**',
        redirectTo: '',
        pathMatch: 'full'
    },
];
